import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import { useMemo } from 'react';
import type { SyncStatusResponse } from '../../../types/backend';

export interface CustomerKBInfo {
  customer_id: string;
  knowledge_base_id: string;
  knowledge_base_bucket: string;
  configured: boolean;
}

export interface CustomerKBDocument {
  document_id: string;
  filename: string;
  size_bytes: number;
  last_modified: string;
}

export interface CustomerKBDocumentList {
  documents: CustomerKBDocument[];
  total_count: number;
  total_size_bytes: number;
}

export interface CustomerKBUploadUrl {
  document_id: string;
  upload_url: string;
  fields: Record<string, string>;
  expires_at: string;
}

export interface CustomerKBDownloadUrl {
  download_url: string;
  expires_at: string;
}

export interface CustomerKBSyncJob {
  job_id: string;
  status: string;
  started_at: string;
}

export interface CustomerKBQueryResult {
  index: number;
  score: number;
  content: string;
  source: string;
}

export interface CustomerKBQueryResponse {
  results: CustomerKBQueryResult[];
  result_count: number;
}

export const createCustomerKBService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getKBInfo: async (customerId: string): Promise<CustomerKBInfo> => {
      return rpcCall<CustomerKBInfo>(client, 'knowledge.CustomerGetKnowledgeBase', { customer_id: customerId });
    },

    listDocuments: async (customerId: string): Promise<CustomerKBDocumentList> => {
      return rpcCall(client, 'knowledge.CustomerListDocuments', { customer_id: customerId });
    },

    getUploadUrl: async (
      customerId: string,
      filename: string,
      contentType: string = 'application/pdf'
    ): Promise<CustomerKBUploadUrl> => {
      return rpcCall(client, 'knowledge.CustomerUploadDocument', { customer_id: customerId, filename, content_type: contentType });
    },

    uploadDocument: async (
      uploadUrl: string,
      fields: Record<string, string>,
      file: File
    ): Promise<void> => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);
      const response = await fetch(uploadUrl, { method: 'POST', body: formData });
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
    },

    getDownloadUrl: async (customerId: string, documentId: string): Promise<CustomerKBDownloadUrl> => {
      return rpcCall<CustomerKBDownloadUrl>(client, 'knowledge.CustomerGetDownloadUrl', { customer_id: customerId, doc_id: documentId });
    },

    deleteDocument: async (customerId: string, documentId: string): Promise<void> => {
      await rpcCall(client, 'knowledge.CustomerDeleteDocument', { customer_id: customerId, doc_id: documentId });
    },

    startSync: async (customerId: string): Promise<CustomerKBSyncJob> => {
      return rpcCall(client, 'knowledge.CustomerStartSync', { customer_id: customerId });
    },

    getSyncStatus: async (customerId: string, jobId: string): Promise<SyncStatusResponse> => {
      return rpcCall(client, 'knowledge.CustomerGetSyncStatus', { customer_id: customerId, job_id: jobId });
    },

    queryKB: async (
      customerId: string,
      query: string,
      numResults: number = 5
    ): Promise<CustomerKBQueryResponse> => {
      return rpcCall<CustomerKBQueryResponse>(client, 'knowledge.CustomerQueryKnowledgeBase', { customer_id: customerId, query, num_results: numResults });
    }
  };
};

export const useCustomerKBService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    return createCustomerKBService(idToken);
  }, [idToken]);
};
